import React from 'react';
import { Card, CardContent, Grid, Stack, Typography, Box, Tooltip } from '@mui/material';
import useRelativeTime from '../hooks/useRelativeTime';

function DeviceCard({ device, onClick }) {
  const { gps_num, machine, receiver_model, ip, online, type, has_issue, issues, last_update, last_update_iso, offline_kind, _placeholder, status, office } = device;

  const isAuthFailed = status === 'auth_failed';

  // Elapsed time since the receiver's last position fix, recomputed in the
  // browser. A null value is meaningful — the receiver reported no valid fix, so
  // it must render as 'Never'. Only fall back to the server-rendered string when
  // the field is absent entirely, i.e. a snapshot cached from before it existed.
  const liveUpdate = useRelativeTime(last_update_iso);
  const displayUpdate = last_update_iso === undefined
    ? (last_update ?? 'Never')
    : liveUpdate;

  // Status dot color based on RTK status
  const getStatusColor = () => {
    if (isAuthFailed) return '#ff8c00';
    if (!online) return '#555555';
    if (type?.includes('RTK Fixed')) return '#00ff41';
    if (type?.includes('RTK Float') || type?.includes('DGPS')) return '#ffbf00';
    return '#ff1a1a';
  };

  // A device that has only just stopped answering is "Currently Unreachable" —
  // possibly a passing cellular dropout, with Last Update showing when it went
  // quiet. Once the backend has seen it miss enough consecutive cycles it grades
  // it to plain "Offline". Older snapshots carry no offline_kind, so fall back to
  // whether we have ever reached the device.
  const offlineLabel = (offline_kind ?? (last_update_iso ? 'unreachable' : 'offline')) === 'unreachable'
    ? 'Currently Unreachable'
    : 'Offline';

  const getStatusLabel = () => {
    if (isAuthFailed) return 'Auth Failed';
    if (!online) return offlineLabel;
    if (type?.includes('RTK Fixed')) return 'RTK Fixed';
    if (type?.includes('RTK Float')) return 'RTK Float';
    if (type?.includes('DGPS')) return 'DGPS';
    return 'No Fix';
  };

  // Friendly issue label
  const formatIssue = (issue) => {
    const duration = issue.duration ? ` — ${issue.duration}` : '';
    switch (issue.type) {
      case 'NTRIP':
        if (issue.status?.includes('Resolving')) return `NTRIP Issue Resolved - Confirming resolution${duration}`;
        return `NTRIP disconnected${duration}`;
      case 'GPS_SATS': {
        if (issue.status?.includes('Resolving')) return `Satellite Issue Resolved - Confirming resolution${duration}`;
        // Name the offending antenna(s) from the structured counts the backend sends.
        const { num_pos_svs: pos, num_vec_svs: vec, min_sats: min } = issue;
        const parts = [];
        // Only annotate when the backend sent structured counts (min_sats present).
        if (min != null) {
          if (pos != null && pos < min) parts.push(`Position antenna (${pos} sats)`);
          if (vec == null) parts.push('Vector antenna not detected');
          else if (vec < min) parts.push(`Vector antenna (${vec} sats)`);
        }
        const detail = parts.length ? ` — ${parts.join(', ')}` : '';
        return `Low satellite signal${detail}${duration}`;
      }
      case 'NTRIP_CONFIG':
        if (issue.status?.includes('Resolving')) return `NTRIP Config Issue Resolved - Confirming resolution${duration}`;
        return `NTRIP not configured${duration}`;
      case 'NTRIP_ACCOUNT':
        return `NTRIP account incomplete${duration}`;
      default:
        return `${issue.message || issue.status}${duration}`;
    }
  };

  // Check if offline for more than 6 hours
  const isStaleOffline = !online && has_issue && issues?.some(
    (i) => i.duration_seconds > 3600
  );

  // Build status display
  const getStatusDisplay = () => {
    if (_placeholder) return '';
    if (isAuthFailed) return 'Login Failed';
    if (!online && has_issue && issues?.length > 0) {
      return (
        <>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {offlineLabel}
          </Typography>
          {issues.map((issue, idx) => (
            <Typography key={idx} variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {formatIssue(issue)}
            </Typography>
          ))}
        </>
      );
    }
    if (!online) return offlineLabel;
    if (has_issue && issues?.length > 0) {
      return issues.map((issue, idx) => (
        <Typography key={idx} variant="body2" sx={{ lineHeight: 1.4 }}>
          {formatIssue(issue)}
        </Typography>
      ));
    }
    return 'Online';
  };

  // During the resolving grace window, every issue.status will start with "Resolving";
  // suppress the red outline in that case so the card looks calm while it confirms.
  const allResolving = has_issue && issues?.length > 0 && issues.every(i => i.status?.includes('Resolving'));
  const showRedOutline = online && has_issue && !allResolving;

  const statusDot = (size) => (
    <Tooltip title={getStatusLabel()} arrow>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${getStatusColor()} 45%, ${getStatusColor()}88 55%, ${getStatusColor()}22 70%, transparent 80%)`,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );

  const ipLink = ip ? (
    <a
      href={`http://${ip}:8001`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.3)' }}
    >
      {ip}
    </a>
  ) : '-';

  return (
    <Card
      onClick={onClick}
      sx={{
        mb: 0.75,
        border: showRedOutline ? 2 : 1,
        borderColor: showRedOutline ? '#FF0000' : 'divider',
        opacity: online ? 1 : 0.7,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        {/* Phone/tablet: the row's columns can't fit side by side, so stack the
            same fields; tapping the card still opens the device details. */}
        <Stack spacing={0.25} sx={{ display: { xs: 'flex', md: 'none' } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {statusDot(20)}
            <Typography variant="body2" fontWeight={700} noWrap>{gps_num}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 'auto', flexShrink: 0 }}>
              {displayUpdate}
            </Typography>
          </Stack>
          <Typography variant="body2" noWrap sx={{ pl: 3.5 }}>
            {machine || '-'}{receiver_model ? ` · ${receiver_model}` : ''}
          </Typography>
          <Box sx={{ pl: 3.5, fontSize: 13 }}>
            {getStatusDisplay()}
            {office && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                Office — not tracked in history
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{ipLink}</Typography>
          </Box>
        </Stack>

        <Grid container spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap', display: { xs: 'none', md: 'flex' } }}>
          {/* Status Dot */}
          <Grid item sx={{ flexShrink: 0, width: 80 }}>
            {statusDot(32)}
          </Grid>

          {/* GPS Number */}
          <Grid item sx={{ flexShrink: 0, width: 140 }}>
            <Typography variant="body1" fontWeight={700} noWrap sx={{ ml: 3 }}>
              {gps_num}
            </Typography>
          </Grid>

          {/* Machine */}
          <Grid item sx={{ flexShrink: 0, width: 200 }}>
            <Typography variant="body1" noWrap>
              {machine || '-'}
            </Typography>
          </Grid>

          {/* Receiver model */}
          <Grid item sx={{ flexShrink: 0, width: 140 }}>
            <Typography variant="body1" noWrap>
              {receiver_model || '-'}
            </Typography>
          </Grid>

          {/* IP Address */}
          <Grid item sx={{ flexShrink: 0, width: 220 }}>
            <Typography variant="body1" noWrap>{ipLink}</Typography>
          </Grid>

          {/* Status */}
          <Grid item sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              {getStatusDisplay()}
              {office && (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontStyle: 'italic' }}>
                  Office — not tracked in history
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Last Update */}
          <Grid item sx={{ flexShrink: 0, width: 160, textAlign: 'right' }}>
            <Typography variant="body1" color="text.secondary" noWrap>
              {displayUpdate}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default DeviceCard;
